import React, { useState, useEffect, Suspense, useRef } from 'react';
import Header from './components/Header.tsx';
import Hero from './components/Hero.tsx';
import SEO from './components/SEO.tsx';
import { GOOGLE_REVIEWS_URL, BUSINESS_ABN, BUSINESS_PHONE, BUSINESS_EMAIL } from './constants.tsx';
import { getSuburbConfig } from './suburbData.ts';
import { getServiceDetail, SERVICE_DETAILS } from './components/serviceData.ts';
import { getServiceBySlug } from './components/landingPageData.ts';
import { getBrandFromSlug, getBrandServiceFromSlug } from './components/brandData.ts'; // NEW IMPORT
import { Language, TRANSLATIONS } from './translations.ts';
import NavigationLink from './components/NavigationLink.tsx';
import { ExternalLink, MessageCircle, Mail, ShieldCheck, Loader2 } from 'lucide-react';

// Lazy Load Home Page Sections to reduce initial bundle size
const Features = React.lazy(() => import('./components/Features.tsx'));
const Pricing = React.lazy(() => import('./components/Pricing.tsx'));
const Services = React.lazy(() => import('./components/Services.tsx'));
const About = React.lazy(() => import('./components/About.tsx'));
const Testimonials = React.lazy(() => import('./components/Testimonials.tsx'));
const Blog = React.lazy(() => import('./components/Blog.tsx'));
const RecentRepairs = React.lazy(() => import('./components/RecentRepairs.tsx'));
const FAQ = React.lazy(() => import('./components/FAQ.tsx'));
const Locations = React.lazy(() => import('./components/Locations.tsx'));
const Privacy = React.lazy(() => import('./components/Privacy.tsx'));
const PostcodeChecker = React.lazy(() => import('./components/PostcodeChecker.tsx'));
const CommunityHero = React.lazy(() => import('./components/CommunityHero.tsx'));
const GoogleBusinessCard = React.lazy(() => import('./components/GoogleBusinessCard.tsx'));

// Lazy Load Fixed Widgets
const ContactDock = React.lazy(() => import('./components/ContactDock.tsx'));
const FloatingContact = React.lazy(() => import('./components/FloatingContact.tsx'));
const BookingModal = React.lazy(() => import('./components/BookingModal.tsx'));

// Lazy Load Pages
const AboutPage = React.lazy(() => import('./components/AboutPage.tsx'));
const ServicesPage = React.lazy(() => import('./components/ServicesPage.tsx'));
const LocationsPage = React.lazy(() => import('./components/LocationsPage.tsx'));
const PricingPage = React.lazy(() => import('./components/PricingPage.tsx'));
const HowItWorksPage = React.lazy(() => import('./components/HowItWorksPage.tsx'));
const StudentSupportPage = React.lazy(() => import('./components/StudentSupportPage.tsx'));
const ServiceDetailPage = React.lazy(() => import('./components/ServiceDetailPage.tsx'));
const SuburbPage = React.lazy(() => import('./components/SuburbPage.tsx'));
const AllSuburbsPage = React.lazy(() => import('./components/AllSuburbsPage.tsx'));
const LandingPage = React.lazy(() => import('./components/LandingPage.tsx'));
const LocationServiceLandingPage = React.lazy(() => import('./components/LocationServiceLandingPage.tsx'));
const BrandLandingPage = React.lazy(() => import('./components/BrandLandingPage.tsx')); // NEW
const BrandServiceDetailPage = React.lazy(() => import('./components/BrandServiceDetailPage.tsx')); // NEW
const AllRepairsPage = React.lazy(() => import('./components/AllRepairsPage.tsx'));
const PrivacyPolicyPage = React.lazy(() => import('./components/PrivacyPolicyPage.tsx'));
const TermsOfServicePage = React.lazy(() => import('./components/TermsOfServicePage.tsx'));
const WarrantyPolicyPage = React.lazy(() => import('./components/WarrantyPolicyPage.tsx'));

const NotFoundPage = React.lazy(() => import('./components/NotFoundPage.tsx'));

interface ErrorBoundaryProps {
  children?: React.ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
}

// Simple Error Boundary
class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  public state: ErrorBoundaryState = { hasError: false };
  public props: ErrorBoundaryProps;

  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.props = props;
  }

  static getDerivedStateFromError(error: any) {
    return { hasError: true };
  }

  componentDidCatch(error: any, errorInfo: any) {
    console.error("ErrorBoundary caught an error", error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-slate-50 p-8 text-center flex-col gap-4">
          <div className="w-16 h-16 bg-red-100 text-red-500 rounded-full flex items-center justify-center text-3xl font-bold mb-4">!</div>
          <h1 className="text-3xl font-black text-navy">Oops! Something went wrong.</h1>
          <p className="text-slate-500 max-w-md">We encountered an unexpected error safely loading this portion of the page. Please try refreshing or contact our team if the issue persists.</p>
          <button onClick={() => window.location.reload()} className="mt-4 bg-blue-600 text-white px-6 py-3 rounded-xl font-bold">Refresh Page</button>
        </div>
      );
    }
    return this.props.children;
  }
}

// Utility Component: Only renders (and loads) children when they are close to the viewport
const DeferredSection = ({ children, fallback = null }: { children?: React.ReactNode, fallback?: React.ReactNode }) => {
  const [isVisible, setIsVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Bots don't trigger intersection observer reliably
    const isBot = /bot|googlebot|crawler|spider|robot|crawling|AhrefsSiteAudit|ahrefsbot/i.test(navigator.userAgent);
    if (isBot) {
      setIsVisible(true);
      return;
    }

    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setIsVisible(true);
        observer.disconnect();
      }
    }, { rootMargin: '300px' }); // Load 300px before appearing

    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <div ref={ref} style={{ minHeight: '50px' }}>
      {isVisible ? (
        <ErrorBoundary>
          <Suspense fallback={fallback}>{children}</Suspense>
        </ErrorBoundary>
      ) : fallback}
    </div>
  );
};

// Utility Component: Delays loading of non-critical fixed widgets until after initial load
const IdleLoader = ({ children }: { children?: React.ReactNode }) => {
  const [shouldLoad, setShouldLoad] = useState(false);
  useEffect(() => {
    const timer = setTimeout(() => setShouldLoad(true), 3000); // 3-second delay
    return () => clearTimeout(timer);
  }, []);
  return shouldLoad ? (
    <ErrorBoundary>
      <Suspense fallback={null}>{children}</Suspense>
    </ErrorBoundary>
  ) : null;
};

const App: React.FC = () => {
  const [isBookingOpen, setIsBookingOpen] = useState(false);
  const [currentView, setCurrentView] = useState<string>('home');
  const [selectedSuburbId, setSelectedSuburbId] = useState<string | null>(null);
  const [selectedServiceId, setSelectedServiceId] = useState<string | null>(null);
  const [dynamicLandingData, setDynamicLandingData] = useState<any>(null);
  const [brandData, setBrandData] = useState<any>(null); // NEW: To store active brand data
  const [brandServiceData, setBrandServiceData] = useState<any>(null); // NEW: To store active brand service data
  const [lang, setLang] = useState<Language>('en');

  const handleUrlChange = (overridePath?: string) => {
    const path = overridePath || window.location.pathname;
    const cleanPath = path.endsWith('/') && path.length > 1 ? path.slice(0, -1) : path;

    if (cleanPath === '/' || cleanPath === '') {
      setCurrentView('home');
      return;
    }
    if (cleanPath === '/about') {
      setCurrentView('about');
      return;
    }
    if (cleanPath === '/services') {
      setCurrentView('services');
      return;
    }
    if (cleanPath === '/locations') {
      setCurrentView('locations');
      return;
    }
    if (cleanPath === '/pricing') {
      setCurrentView('pricing');
      return;
    }
    if (cleanPath === '/how-it-works') {
      setCurrentView('how-it-works');
      return;
    }
    if (cleanPath === '/student-faculty') {
      setCurrentView('student-faculty');
      return;
    }
    if (cleanPath === '/privacy-policy') {
      setCurrentView('privacy-policy');
      return;
    }
    if (cleanPath === '/terms-of-service') {
      setCurrentView('terms-of-service');
      return;
    }
    if (cleanPath === '/warranty-policy') {
      setCurrentView('warranty-policy');
      return;
    }
    if (cleanPath === '/all-suburbs') {
      setCurrentView('all-suburbs');
      return;
    }
    if (cleanPath === '/repairs') {
      setCurrentView('repairs');
      return;
    }

    const slug = cleanPath.substring(1);

    if (slug === 'newfarm-liquid-damage-repair') {
      setCurrentView('landing-newfarm-liquid');
      return;
    }

    if (SERVICE_DETAILS[slug]) {
        setSelectedServiceId(slug);
        setCurrentView('service-detail');
        return;
    }

    // 1. Check for Brand Hub Page (e.g. /lenovo-laptop-repairs)
    const brandInfo = getBrandFromSlug(slug);
    if (brandInfo) {
      setBrandData(brandInfo);
      setCurrentView('brand-landing');
      return;
    }

    // 2. Check for Brand Specific Service Page (e.g. /lenovo-screen-replacement)
    const brandServiceInfo = getBrandServiceFromSlug(slug);
    if (brandServiceInfo) {
      setBrandServiceData(brandServiceInfo);
      setCurrentView('brand-service-detail');
      return;
    }

    // 3. Check for Location+Service Page (e.g. newstead-data-recovery)
    const dynamicLanding = getServiceBySlug(slug);
    if (dynamicLanding) {
      setDynamicLandingData(dynamicLanding);
      setCurrentView('dynamic-location-service');
      return;
    }

    if (slug && !slug.includes('/')) {
        const suburbConfig = getSuburbConfig(slug);
        if (suburbConfig) {
            setSelectedSuburbId(slug);
            setCurrentView('suburb');
            return;
        }
    }

    setCurrentView('not-found');
  };

  useEffect(() => {
    handleUrlChange();
    const onPopState = () => handleUrlChange();
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, []);

  useEffect(() => {
    if (window.location.hash) {
      const id = window.location.hash.substring(1);
      setTimeout(() => {
        const element = document.getElementById(id);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 300);
    }
  }, [currentView]);

  const pushRoute = (path: string) => {
    try {
      window.history.pushState({}, '', path);
    } catch (e) {
      console.warn("Navigation warning: History API restricted, falling back to internal routing.");
    }
    const hasHash = path.includes('#');
    handleUrlChange(path.split('#')[0]); // Only pass the path part
    
    if (hasHash) {
      const id = path.split('#')[1];
      setTimeout(() => {
        const element = document.getElementById(id);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
    } else {
      window.scrollTo(0, 0);
    }
  };

  const openBooking = () => setIsBookingOpen(true);
  const closeBooking = () => setIsBookingOpen(false);
  
  const navigateToHome = () => pushRoute('/');
  const navigateToAbout = () => pushRoute('/about');
  const navigateToServices = () => pushRoute('/services');
  const navigateToLocations = () => pushRoute('/locations');
  const navigateToPricing = () => pushRoute('/pricing');
  const navigateToHowItWorks = () => pushRoute('/how-it-works');
  const navigateToStudentSupport = () => pushRoute('/student-faculty');
  const navigateToPrivacyPolicy = () => pushRoute('/privacy-policy');
  const navigateToTermsOfService = () => pushRoute('/terms-of-service');
  const navigateToWarrantyPolicy = () => pushRoute('/warranty-policy');
  
  const navigateToSuburb = (id: string) => {
    pushRoute(`/${id}`);
  };

  const navigateToServiceDetail = (id: string) => {
    pushRoute(`/${id}`);
  };

  const navigateToBrand = (slug: string) => {
    pushRoute(`/${slug}`);
  };

  const t = TRANSLATIONS[lang];
  const phoneStripped = BUSINESS_PHONE.replace(/\s/g, '');
  const whatsappUrl = `https://wa.me/61${phoneStripped.slice(1)}`;

  const LoadingFallback = () => (
    <div className="min-h-[60vh] flex flex-col items-center justify-center bg-slate-50">
      <Loader2 className="w-12 h-12 text-green-700 animate-spin mb-4" />
      <p className="text-navy font-bold animate-pulse text-lg">Loading...</p>
    </div>
  );

  const renderContent = () => {
    switch (currentView) {
      case 'about':
        return <AboutPage onBack={navigateToHome} onOpenBooking={openBooking} />;
      case 'services':
        return (
          <ServicesPage 
            onBack={navigateToHome} 
            onOpenBooking={openBooking} 
            onNavigateStudentSupport={navigateToStudentSupport} 
            onNavigateServiceDetail={navigateToServiceDetail}
            onNavigateBrand={navigateToBrand} // NEW PROP
          />
        );
      case 'locations':
        return <LocationsPage onBack={navigateToHome} onOpenBooking={openBooking} onNavigateSuburb={navigateToSuburb} />;
      case 'pricing':
        return <PricingPage onBack={navigateToHome} onOpenBooking={openBooking} />;
      case 'how-it-works':
        return <HowItWorksPage onBack={navigateToHome} onOpenBooking={openBooking} />;
      case 'student-faculty':
        return <StudentSupportPage onBack={navigateToServices} onOpenBooking={openBooking} lang={lang} />;
      case 'privacy-policy':
        return <PrivacyPolicyPage onBack={navigateToHome} />;
      case 'terms-of-service':
        return <TermsOfServicePage onBack={navigateToHome} />;
      case 'warranty-policy':
        return <WarrantyPolicyPage onBack={navigateToHome} />;
      case 'all-suburbs':
        return <AllSuburbsPage onBack={navigateToLocations} onOpenBooking={openBooking} onNavigateSuburb={navigateToSuburb} />;
      case 'repairs':
        return <AllRepairsPage onBack={navigateToHome} onOpenBooking={openBooking} />;
      case 'service-detail':
        if (!selectedServiceId) return null;
        return <ServiceDetailPage service={getServiceDetail(selectedServiceId)} onBack={navigateToServices} onOpenBooking={openBooking} />;
      case 'suburb':
        if (!selectedSuburbId) return null;
        return <SuburbPage config={getSuburbConfig(selectedSuburbId)} onBack={navigateToLocations} onOpenBooking={openBooking} />;
      case 'landing-newfarm-liquid':
        return <LandingPage onBack={navigateToHome} onOpenBooking={openBooking} />;
      case 'dynamic-location-service':
        if (!dynamicLandingData) return null;
        return (
          <LocationServiceLandingPage 
            suburbName={dynamicLandingData.formattedSuburb}
            suburbSlug={dynamicLandingData.suburbName} 
            serviceConfig={dynamicLandingData.service}
            onBack={navigateToHome}
            onOpenBooking={openBooking}
          />
        );
      case 'brand-landing':
        if (!brandData) return null;
        return (
          <BrandLandingPage 
            brand={brandData} 
            onBack={navigateToServices} 
            onOpenBooking={openBooking}
            onNavigateDetail={(slug) => pushRoute(`/${slug}`)}
          />
        );
      case 'brand-service-detail':
        if (!brandServiceData) return null;
        return (
          <BrandServiceDetailPage 
            brand={brandServiceData.brand}
            service={brandServiceData.service}
            onBack={() => pushRoute(`/${brandServiceData.brand.slug}-laptop-repairs`)}
            onOpenBooking={openBooking}
          />
        );
      case 'not-found':
      default:
        return <NotFoundPage />;
      case 'home':
        return (
          <>
            <SEO 
              title="Brisbane Computer Fix | Ike - Trusted Local Expert Computer Repair" 
              description="Trusted, senior-friendly computer repair in Brisbane. $170 fixed labor, No Fix No Fee. Specialized in MacBook, Data Recovery and Senior Security. Call Ike today."
              schemas={[
                {
                  "@context": "https://schema.org",
                  "@type": "ComputerStore",
                  "name": "Brisbane Computer Fix",
                  "image": "https://brisbanecomputerrepair.com/favicon.svg",
                  "@id": "https://brisbanecomputerrepair.com",
                  "url": "https://brisbanecomputerrepair.com",
                  "telephone": BUSINESS_PHONE,
                  "priceRange": "$$",
                  "address": {
                    "@type": "PostalAddress",
                    "streetAddress": "Indooroopilly",
                    "addressLocality": "Brisbane",
                    "addressRegion": "QLD",
                    "postalCode": "4068",
                    "addressCountry": "AU"
                  },
                  "aggregateRating": {
                    "@type": "AggregateRating",
                    "ratingValue": "4.9",
                    "reviewCount": "1000",
                    "bestRating": "5",
                    "worstRating": "1"
                  },
                  "geo": {
                    "@type": "GeoCoordinates",
                    "latitude": -27.5000,
                    "longitude": 152.9710
                  },
                  "openingHoursSpecification": {
                    "@type": "OpeningHoursSpecification",
                    "dayOfWeek": [
                      "Monday",
                      "Tuesday",
                      "Wednesday",
                      "Thursday",
                      "Friday",
                      "Saturday",
                      "Sunday"
                    ],
                    "opens": "08:00",
                    "closes": "21:00"
                  },
                  "founder": {
                    "@type": "Person",
                    "name": "Ike"
                  }
                }
              ]}
            />
            
            {/* Critical Content Loaded Eagerly */}
            <Hero onOpenBooking={openBooking} onNavigatePricing={navigateToPricing} lang={lang} />
            
            {/* Deferred Content Loaded on Scroll */}
            <DeferredSection>
              <Features />
            </DeferredSection>
            
            <section className="bg-slate-50 py-24 px-6 overflow-hidden">
              <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
                <div className="space-y-8">
                   <div className="inline-flex items-center gap-3 bg-white text-navy px-5 py-2 rounded-full text-xs font-black tracking-widest uppercase border border-slate-200">
                     <ShieldCheck size={16} className="text-green-700" />
                     Official Business Channel
                   </div>
                   <h2 className="text-4xl md:text-5xl font-black text-navy leading-tight">
                     Chat Directly on <br/>
                     <span className="text-blue-600">Google Business.</span>
                   </h2>
                   <p className="text-xl text-slate-600 leading-relaxed max-w-lg">
                     We're integrated with Google's verified messaging platform. Message Ike instantly for technical advice, pricing, or to share photos of your computer issue.
                   </p>
                   <div className="flex flex-wrap gap-4">
                      <a href={GOOGLE_REVIEWS_URL} target="_blank" rel="noopener noreferrer" className="bg-blue-600 text-white px-8 py-4 rounded-2xl font-black shadow-xl hover:bg-blue-700 transition-all flex items-center gap-2">
                        Message on Google <ExternalLink size={18} />
                      </a>
                   </div>
                </div>
                <div className="relative">
                   <div className="absolute inset-0 bg-blue-500/10 rounded-[4rem] blur-[100px] -z-10 animate-pulse"></div>
                   <DeferredSection fallback={<div className="h-[400px] bg-white/50 rounded-[2.5rem]" />}>
                      <GoogleBusinessCard />
                   </DeferredSection>
                </div>
              </div>
            </section>

            <DeferredSection>
              <Pricing onNavigateDetail={navigateToPricing} />
            </DeferredSection>
            
            <section className="bg-navy py-16 text-white text-center px-4 relative overflow-hidden">
              <div className="absolute top-0 right-0 p-12 opacity-[0.05] pointer-events-none">
                <ShieldCheck size={200} />
              </div>
              <div className="max-w-4xl mx-auto relative z-10">
                <h2 className="text-3xl md:text-4xl font-bold mb-6 italic">"Technology should work for you, not frustrate you."</h2>
                <p className="text-xl opacity-90 mb-8 font-light">– Ike, Lead Technician</p>
                <div className="flex flex-wrap justify-center gap-6">
                  <a 
                    href={GOOGLE_REVIEWS_URL} 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="bg-white/10 px-4 py-2 rounded-full border border-white/20 hover:bg-white/20 transition-colors flex items-center gap-2"
                  >
                    ⭐ 4.9/5 Google Rating <ExternalLink size={14} className="opacity-50" />
                  </a>
                  <span className="bg-white/10 px-4 py-2 rounded-full border border-white/20">🛠️ Master Technician</span>
                  <span className="bg-white/10 px-4 py-2 rounded-full border border-white/20">📍 Local Brisbane Business</span>
                </div>
              </div>
            </section>

            <DeferredSection>
              <PostcodeChecker onOpenBooking={openBooking} onNavigateSuburb={navigateToSuburb} />
            </DeferredSection>
            
            <DeferredSection>
              <Services onNavigateCatalog={navigateToServices} />
            </DeferredSection>
            
            <DeferredSection>
              <About onNavigateDetail={navigateToAbout} lang={lang} />
            </DeferredSection>
            
            <DeferredSection>
              <Testimonials />
            </DeferredSection>
            
            <DeferredSection>
              <RecentRepairs />
            </DeferredSection>

            <DeferredSection>
              <CommunityHero />
            </DeferredSection>
            
            <DeferredSection>
              <Blog />
            </DeferredSection>
            
            <DeferredSection>
              <FAQ />
            </DeferredSection>
            
            <DeferredSection>
              <Privacy />
            </DeferredSection>
            
            <DeferredSection>
              <Locations onNavigateDetail={navigateToLocations} />
            </DeferredSection>
          </>
        );
    }
  };

  return (
    <div className="min-h-screen flex flex-col selection:bg-yellow-200">
      <a 
        href="#main-content" 
        className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:z-[200] focus:bg-white focus:text-navy focus:px-6 focus:py-3 focus:rounded-xl focus:shadow-2xl focus:font-bold focus:border-2 focus:border-green-600 focus:outline-none focus:ring-4 focus:ring-green-500/50"
      >
        Skip to main content
      </a>

      <Header 
        onOpenBooking={openBooking} 
        onNavigateAbout={navigateToAbout} 
        onNavigateServices={navigateToServices} 
        onNavigateLocations={navigateToLocations}
        onNavigatePricing={navigateToPricing}
        onNavigateHome={navigateToHome} 
        isHome={currentView === 'home'} 
        currentView={currentView}
        lang={lang}
        onLangChange={setLang}
      />
      
      <main id="main-content" className="flex-grow focus:outline-none" tabIndex={-1}>
        <ErrorBoundary>
          <Suspense fallback={<LoadingFallback />}>
            {renderContent()}
          </Suspense>
        </ErrorBoundary>
      </main>

      <footer className="bg-slate-900 text-white py-12 px-6">
        <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-12">
          <div>
            <h3 className="text-xl font-bold mb-4">Brisbane Computer Fix</h3>
            <p className="text-slate-400 leading-relaxed">
              {t.footer_tagline}
            </p>
          </div>
          <div>
            <h3 className="text-lg font-semibold mb-4">Quick Links</h3>
            <ul className="space-y-2 text-slate-400 flex flex-col items-start">
              <li><NavigationLink href="/about" onClick={navigateToAbout} className="hover:text-green-400 transition-colors">{t.nav_about}</NavigationLink></li>
              <li><NavigationLink href="/services" onClick={navigateToServices} className="hover:text-green-400 transition-colors">{t.nav_services}</NavigationLink></li>
              <li><NavigationLink href="/repairs" onClick={() => pushRoute('/repairs')} className="hover:text-green-400 transition-colors">Recent Repairs</NavigationLink></li>
              <li><NavigationLink href="/locations" onClick={navigateToLocations} className="hover:text-green-400 transition-colors">{t.nav_locations}</NavigationLink></li>
              <li><NavigationLink href="/all-suburbs" onClick={() => pushRoute('/all-suburbs')} className="hover:text-green-400 transition-colors">All Service Areas</NavigationLink></li>
              <li><NavigationLink href="/student-faculty" onClick={navigateToStudentSupport} className="text-green-400 font-bold hover:underline transition-all">Student Discount</NavigationLink></li>
              <li><NavigationLink href="/how-it-works" onClick={navigateToHowItWorks} className="hover:text-green-400 transition-colors underline decoration-green-400">Repair Process</NavigationLink></li>
              <li><NavigationLink href="/#faq" onClick={() => pushRoute('/#faq')} className="hover:text-green-400 transition-colors">FAQs</NavigationLink></li>
              <li className="pt-4 mt-4 border-t border-slate-800"><NavigationLink href="/privacy-policy" onClick={navigateToPrivacyPolicy} className="text-sm hover:text-green-400 transition-colors">Privacy Policy</NavigationLink></li>
              <li><NavigationLink href="/terms-of-service" onClick={navigateToTermsOfService} className="text-sm hover:text-green-400 transition-colors">Terms of Service</NavigationLink></li>
              <li><NavigationLink href="/warranty-policy" onClick={navigateToWarrantyPolicy} className="text-sm hover:text-green-400 transition-colors">Warranty & Refund Policy</NavigationLink></li>
            </ul>
          </div>
          <div>
            <h3 className="text-lg font-semibold mb-4">Support & Trust</h3>
            <p className="text-slate-400 mb-4">{t.footer_emergency}</p>
            <div className="flex flex-col gap-4">
              <a href={`tel:${phoneStripped}`} className="text-2xl font-bold text-green-400 hover:text-green-300 transition-colors">
                {BUSINESS_PHONE}
              </a>
              <div className="flex flex-col gap-3">
                <a 
                  href={whatsappUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 bg-[#25D366]/10 text-[#25D366] px-4 py-2 rounded-xl border border-[#25D366]/20 hover:bg-[#25D366]/20 transition-all font-bold w-fit"
                >
                  <MessageCircle size={20} fill="currentColor" className="fill-current" />
                  {t.whatsapp} Ike
                </a>
                <a 
                  href={`mailto:${BUSINESS_EMAIL}`}
                  className="inline-flex items-center gap-2 bg-blue-500/10 text-blue-400 px-4 py-2 rounded-xl border border-blue-500/20 hover:bg-blue-500/20 transition-all font-bold w-fit"
                >
                  <Mail size={20} />
                  {BUSINESS_EMAIL}
                </a>
              </div>
            </div>
          </div>
        </div>
        <div className="max-w-7xl mx-auto mt-12 pt-8 border-t border-slate-800 text-center text-slate-500 text-sm">
          © {new Date().getFullYear()} Brisbane Computer Fix. All Rights Reserved. ABN: {BUSINESS_ABN}
        </div>
      </footer>

      {/* Non-critical widgets loaded on idle */}
      <IdleLoader>
        <ContactDock />
      </IdleLoader>
      
      <IdleLoader>
        <FloatingContact />
      </IdleLoader>
      
      <Suspense fallback={null}>
        <BookingModal isOpen={isBookingOpen} onClose={closeBooking} />
      </Suspense>
    </div>
  );
};

export default App;